import { getAudioContext, isMuted, unlockAudio } from './sfxCore.js';

/**
 * Arcade loop I–V–vi–IV @ 120 BPM — evolves over ~60s then cycles variants at peak energy.
 */
const BPM = 120;
const BEAT_SEC = 60 / BPM;
const BEATS_PER_CHORD = 4;
const CHORD_SEC = BEAT_SEC * BEATS_PER_CHORD;
const MAX_STAGE = 5;
const STAGE_EVERY_CYCLES = 2;

/** @typedef {{ root: number, chord: number[], melodies: number[][], counter?: number[] }} ChordBlock */

/** @type {readonly ChordBlock[]} */
const SCORE = Object.freeze([
    {
        root: 261.63,
        chord: [261.63, 329.63, 392.0],
        melodies: [
            [523.25, 659.25, 783.99, 659.25],
            [659.25, 783.99, 987.77, 783.99],
            [523.25, 523.25, 659.25, 783.99],
        ],
        counter: [392.0, 440.0, 493.88, 440.0],
    },
    {
        root: 196.0,
        chord: [196.0, 246.94, 293.66],
        melodies: [
            [392.0, 493.88, 587.33, 493.88],
            [493.88, 587.33, 739.99, 587.33],
            [392.0, 440.0, 493.88, 587.33],
        ],
        counter: [293.66, 329.63, 369.99, 329.63],
    },
    {
        root: 220.0,
        chord: [220.0, 261.63, 329.63],
        melodies: [
            [440.0, 523.25, 659.25, 523.25],
            [523.25, 659.25, 783.99, 659.25],
            [440.0, 493.88, 523.25, 659.25],
        ],
        counter: [329.63, 369.99, 440.0, 392.0],
    },
    {
        root: 174.61,
        chord: [174.61, 220.0, 261.63],
        melodies: [
            [349.23, 440.0, 523.25, 440.0],
            [440.0, 523.25, 659.25, 523.25],
            [349.23, 392.0, 440.0, 523.25],
        ],
        counter: [261.63, 293.66, 329.63, 293.66],
    },
]);

const LOOP_SECONDS = CHORD_SEC * SCORE.length;

/** @type {GainNode | null} */
let masterGain = null;
/** @type {DynamicsCompressorNode | null} */
let compressor = null;
/** @type {ReturnType<typeof setTimeout> | null} */
let loopTimer = null;
let running = false;
let cycleIndex = 0;

/**
 * @param {number} cycle
 * @returns {{
 *   stage: number,
 *   variant: number,
 *   pad: boolean,
 *   melody: boolean,
 *   arpeggio: boolean,
 *   counter: boolean,
 *   brass: boolean,
 *   driveBass: boolean,
 *   openHats: boolean,
 *   octaveUp: boolean,
 *   drumIntensity: number,
 *   arpeggioSteps: number,
 *   fillLast: boolean,
 *   padDetune: boolean,
 * }}
 */
function getEvolution(cycle) {
    const stage = Math.min(MAX_STAGE, Math.floor(cycle / STAGE_EVERY_CYCLES));
    const variant = Math.floor(cycle / 3) % SCORE[0].melodies.length;
    const atPeak = stage >= MAX_STAGE;

    return {
        stage,
        variant,
        pad: true,
        melody: stage >= 1,
        arpeggio: stage >= 2,
        counter: stage >= 3,
        brass: stage >= 4,
        driveBass: stage >= 2,
        openHats: stage >= 3,
        octaveUp: atPeak && variant === 2,
        drumIntensity: 0.78 + stage * 0.045,
        arpeggioSteps: stage >= 4 ? 12 : 8,
        fillLast: stage >= 4 && cycle % 4 === 3,
        padDetune: stage >= 3,
    };
}

function connectToMaster(node) {
    if (masterGain) node.connect(masterGain);
}

function expEnv(gainNode, startTime, attack, peak, releaseEnd) {
    gainNode.gain.setValueAtTime(0.0001, startTime);
    gainNode.gain.exponentialRampToValueAtTime(Math.max(peak, 0.0002), startTime + attack);
    gainNode.gain.exponentialRampToValueAtTime(0.0001, releaseEnd);
}

function scheduleKick(ctx, startTime, intensity = 1) {
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(150, startTime);
    osc.frequency.exponentialRampToValueAtTime(50, startTime + 0.08);
    expEnv(g, startTime, 0.003, 0.12 * intensity, startTime + 0.11);
    osc.connect(g);
    connectToMaster(g);
    osc.start(startTime);
    osc.stop(startTime + 0.12);
}

function scheduleSnare(ctx, startTime, intensity = 1) {
    const dur = 0.14;
    const bufferSize = Math.floor(ctx.sampleRate * dur);
    const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i += 1) {
        data[i] = (Math.random() * 2 - 1) * (1 - i / bufferSize);
    }
    const noise = ctx.createBufferSource();
    noise.buffer = buffer;
    const filter = ctx.createBiquadFilter();
    filter.type = 'highpass';
    filter.frequency.value = 1800;
    const g = ctx.createGain();
    expEnv(g, startTime, 0.002, 0.07 * intensity, startTime + dur);
    noise.connect(filter).connect(g);
    connectToMaster(g);
    noise.start(startTime);
    noise.stop(startTime + dur);
}

function scheduleHat(ctx, startTime, open = false) {
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    const filter = ctx.createBiquadFilter();
    filter.type = 'highpass';
    filter.frequency.value = 8000;
    osc.type = 'square';
    osc.frequency.value = 10000;
    const peak = open ? 0.02 : 0.014;
    const dur = open ? 0.09 : 0.035;
    expEnv(g, startTime, 0.001, peak, startTime + dur);
    osc.connect(filter).connect(g);
    connectToMaster(g);
    osc.start(startTime);
    osc.stop(startTime + dur + 0.01);
}

function scheduleTomFill(ctx, startTime) {
    [220, 165, 130].forEach((hz, i) => {
        const t = startTime + i * BEAT_SEC * 0.25;
        const osc = ctx.createOscillator();
        const g = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.setValueAtTime(hz, t);
        osc.frequency.exponentialRampToValueAtTime(hz * 0.75, t + 0.1);
        expEnv(g, t, 0.003, 0.075, t + 0.12);
        osc.connect(g);
        connectToMaster(g);
        osc.start(t);
        osc.stop(t + 0.14);
    });
}

function scheduleDrums(ctx, blockStart, evolution, isLastChord) {
    const { drumIntensity, openHats, fillLast } = evolution;
    const fullKit = evolution.stage >= 1;

    [0, 1, 2, 3].forEach((beat) => {
        const t = blockStart + beat * BEAT_SEC;
        if (beat === 0 || beat === 2 || fullKit) {
            if (beat === 0 || beat === 2) scheduleKick(ctx, t, drumIntensity);
        }
        if (fullKit && (beat === 1 || beat === 3)) {
            scheduleSnare(ctx, t, drumIntensity);
        }
        scheduleHat(ctx, t);
        scheduleHat(ctx, t + BEAT_SEC * 0.5, openHats && beat % 2 === 1);
    });

    if (fillLast && isLastChord) {
        scheduleTomFill(ctx, blockStart + BEAT_SEC * 2.5);
    }
}

function scheduleBass(ctx, rootHz, blockStart, evolution) {
    const beats = evolution.driveBass ? [0, 1, 2, 3] : [0, 2];
    const peak = 0.055 + evolution.stage * 0.004;

    beats.forEach((beat) => {
        const t = blockStart + beat * BEAT_SEC;
        const osc = ctx.createOscillator();
        const g = ctx.createGain();
        const filter = ctx.createBiquadFilter();
        filter.type = 'lowpass';
        filter.frequency.value = 380 + evolution.stage * 40;
        osc.type = evolution.stage >= 4 ? 'sawtooth' : 'triangle';
        osc.frequency.value = rootHz * 0.5;
        expEnv(g, t, 0.012, peak, t + BEAT_SEC * (evolution.driveBass ? 0.42 : 0.85));
        osc.connect(filter).connect(g);
        connectToMaster(g);
        osc.start(t);
        osc.stop(t + BEAT_SEC * 0.9);
    });
}

function schedulePad(ctx, chord, blockStart, durationSec, evolution) {
    const detunes = evolution.padDetune ? [-5, 0, 5] : [0];

    chord.forEach((freq, i) => {
        detunes.forEach((cents) => {
            const osc = ctx.createOscillator();
            const g = ctx.createGain();
            const filter = ctx.createBiquadFilter();
            filter.type = 'lowpass';
            filter.frequency.value = 2000 + evolution.stage * 120;
            osc.type = 'sine';
            osc.frequency.value = freq * 2 ** (cents / 1200);
            const peak = (0.02 - i * 0.004) / detunes.length;
            g.gain.setValueAtTime(0.0001, blockStart);
            g.gain.exponentialRampToValueAtTime(peak, blockStart + 0.15);
            g.gain.setValueAtTime(peak * 0.88, blockStart + durationSec - 0.12);
            g.gain.exponentialRampToValueAtTime(0.0001, blockStart + durationSec);
            osc.connect(filter).connect(g);
            connectToMaster(g);
            osc.start(blockStart);
            osc.stop(blockStart + durationSec + 0.05);
        });
    });
}

function scheduleBrass(ctx, chord, startTime) {
    chord.slice(0, 3).forEach((freq, i) => {
        const osc = ctx.createOscillator();
        const g = ctx.createGain();
        const filter = ctx.createBiquadFilter();
        filter.type = 'lowpass';
        filter.frequency.setValueAtTime(1800, startTime);
        filter.frequency.exponentialRampToValueAtTime(800, startTime + 0.28);
        osc.type = 'sawtooth';
        osc.frequency.value = freq;
        const t = startTime + i * 0.01;
        expEnv(g, t, 0.02, 0.032 - i * 0.006, t + 0.3);
        osc.connect(filter).connect(g);
        connectToMaster(g);
        osc.start(t);
        osc.stop(t + 0.32);
    });
}

function scheduleMelodyNote(ctx, freq, startTime, gain = 0.028) {
    const dur = BEAT_SEC * 0.82;
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = 5000;
    osc.type = 'triangle';
    osc.frequency.value = freq;
    expEnv(g, startTime, 0.01, gain, startTime + dur);
    osc.connect(filter).connect(g);
    connectToMaster(g);
    osc.start(startTime);
    osc.stop(startTime + dur + 0.02);
}

function scheduleMelody(ctx, notes, blockStart, gain, octaveMul = 1) {
    notes.forEach((freq, beat) => {
        scheduleMelodyNote(ctx, freq * octaveMul, blockStart + beat * BEAT_SEC, gain);
    });
}

function scheduleArpeggio(ctx, chord, blockStart, durationSec, steps) {
    for (let i = 0; i < steps; i += 1) {
        const freq = chord[i % chord.length] * 2;
        const t = blockStart + (i / steps) * durationSec;
        const osc = ctx.createOscillator();
        const g = ctx.createGain();
        osc.type = 'triangle';
        osc.frequency.value = freq;
        expEnv(g, t, 0.006, 0.009, t + durationSec / steps);
        osc.connect(g);
        connectToMaster(g);
        osc.start(t);
        osc.stop(t + durationSec / steps + 0.02);
    }
}

function scheduleChordBlock(ctx, block, blockStart, chordIndex, evolution) {
    const durationSec = CHORD_SEC * 0.98;
    const isLastChord = chordIndex === SCORE.length - 1;
    const melodyLine = block.melodies[evolution.variant] ?? block.melodies[0];
    const octaveMul = evolution.octaveUp ? 2 : 1;
    const melodyGain = 0.022 + evolution.stage * 0.003;

    scheduleDrums(ctx, blockStart, evolution, isLastChord);
    scheduleBass(ctx, block.root, blockStart, evolution);

    if (evolution.pad) {
        schedulePad(ctx, block.chord, blockStart, durationSec, evolution);
    }

    if (evolution.brass && (chordIndex === 0 || isLastChord)) {
        scheduleBrass(ctx, block.chord, blockStart + BEAT_SEC * 0.02);
    }

    if (evolution.melody) {
        scheduleMelody(ctx, melodyLine, blockStart, melodyGain, octaveMul);
    }

    if (evolution.counter && block.counter) {
        scheduleMelody(
            ctx,
            block.counter,
            blockStart + BEAT_SEC * 0.5,
            melodyGain * 0.72,
            octaveMul,
        );
    }

    if (evolution.arpeggio) {
        scheduleArpeggio(ctx, block.chord, blockStart, durationSec, evolution.arpeggioSteps);
    }
}

function scheduleLoop() {
    const ctx = getAudioContext();
    if (!ctx || !running || isMuted() || !masterGain) return;

    const evolution = getEvolution(cycleIndex);
    const start = ctx.currentTime + 0.05;

    const masterLevel = 0.34 + evolution.stage * 0.014;
    masterGain.gain.setTargetAtTime(masterLevel, start, 0.08);

    SCORE.forEach((block, index) => {
        scheduleChordBlock(ctx, block, start + index * CHORD_SEC, index, evolution);
    });

    cycleIndex += 1;

    loopTimer = setTimeout(
        () => {
            if (running && !isMuted()) scheduleLoop();
        },
        LOOP_SECONDS * 1000 - 40,
    );
}

/**
 * Start a quiet procedural background loop while the round runs.
 */
export function startGameMusic() {
    if (running || isMuted()) return;

    const ctx = getAudioContext();
    if (!ctx) return;

    unlockAudio();
    running = true;
    cycleIndex = 0;

    if (!compressor) {
        compressor = ctx.createDynamicsCompressor();
        compressor.threshold.value = -20;
        compressor.knee.value = 12;
        compressor.ratio.value = 2.5;
        compressor.attack.value = 0.008;
        compressor.release.value = 0.12;
        compressor.connect(ctx.destination);
    }

    if (!masterGain) {
        masterGain = ctx.createGain();
        masterGain.gain.value = 0.34;
        masterGain.connect(compressor);
    }

    scheduleLoop();
}

/** Stop background music and release the loop timer. */
export function stopGameMusic() {
    running = false;
    cycleIndex = 0;

    if (loopTimer != null) {
        clearTimeout(loopTimer);
        loopTimer = null;
    }

    if (masterGain) {
        try {
            masterGain.disconnect();
        } catch {
            // already disconnected
        }
        masterGain = null;
    }
}

export function isGameMusicRunning() {
    return running;
}
